import { Plus, Building2, Users, Briefcase, LayoutDashboard, LogOut, Layers, History, GraduationCap, DollarSign } from 'lucide-react';
import { signOut } from '../lib/auth';
import { ThemeToggle } from './ui/ThemeToggle';

const COLLAPSED_W = 64;
const EXPANDED_W  = 248;

// URL valida = comeca com http(s). Placeholder 'ei johnny' (e qualquer
// outro lixo) cai fora.
const isValidUrl = (u) => /^https?:\/\//i.test(String(u || '').trim());

export function Sidebar({
  current, setCurrent, counts, userEmail, hideEmpresas,
  // legacyApps: array de { label, url } com os links pros apps antigos.
  // Em principal recebe a lista completa de constants.LEGACY_APPS; em
  // replica recebe um unico item ({ label: 'Versão anterior', url: ... }).
  legacyApps = [],
  // onboardingUrl: link pra apresentacao/treinamento da replica corrente.
  // Configurado em replicas.config.js (campo onboardingUrl). Aparece como
  // item 'Onboarding' no menu — abre em nova aba.
  onboardingUrl = null,
  // Liga o item "Câmbio" no menu. Hoje so a replica LLCs ativa essa flag.
  hasCambioModule = false,
}) {
  const handleLogout = async () => {
    try { await signOut(); } catch { /* noop */ }
  };

  const initials = (userEmail || '?')
    .split('@')[0]
    .split(/[._-]/)
    .filter(Boolean)
    .map(p => p[0]?.toUpperCase())
    .slice(0, 2)
    .join('') || '?';
  const displayName = (userEmail || '').split('@')[0].replace(/[._-]+/g, ' ');

  // Cada legacy app valido (URL http(s)) vira um item inline na sidebar
  // posicionado DEPOIS de Servicos. Icone unico (History — relogio com
  // seta pra tras, "voltar pra versao anterior") pra todos.
  const legacyItems = (legacyApps || [])
    .filter(a => isValidUrl(a?.url))
    .map((a, i) => ({
      key: `legacy-${i}-${a.label}`,
      type: 'external',
      label: a.label,
      icon: History,
      href: a.url,
    }));

  // Onboarding: aceita URL absoluta (http(s)) OU caminho relativo
  // (/onboarding-bhaz.html). Truthy ja conta — config controla.
  const onboardingItem = onboardingUrl
    ? {
        key: 'onboarding',
        type: 'external',
        label: 'Onboarding',
        icon: GraduationCap,
        href: String(onboardingUrl),
      }
    : null;

  const items = [
    { key: 'home',         label: 'Solicitações',     icon: LayoutDashboard, badge: counts.solicitacoes },
    { key: 'nova',         label: 'Nova solicitação', icon: Plus,            accent: true },
    !hideEmpresas && {
      key: 'empresas',     label: 'Empresas',         icon: Building2,       badge: counts.empresas
    },
    !hideEmpresas && {
      key: 'unidades',     label: 'Unidades',         icon: Layers,          badge: counts.unidades
    },
    { key: 'tomadores',    label: 'Tomadores',        icon: Users,           badge: counts.tomadores },
    { key: 'servicos',     label: 'Serviços',         icon: Briefcase,       badge: counts.servicos },
    hasCambioModule && {
      key: 'cambio',       label: 'Câmbio',           icon: DollarSign,      badge: counts.cambio
    },
    ...legacyItems,
    onboardingItem,
  ].filter(Boolean);

  return (
    <aside
      className="sidebar"
      style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: COLLAPSED_W,
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 10,
        transition: 'width 0.25s ease, box-shadow 0.25s ease',
      }}
    >
      <nav style={{ padding: 12, flex: 1, marginTop: 8 }}>
        {items.map(it => {
          // Item externo (link pra app antigo) — renderiza <a> em vez de <button>.
          if (it.type === 'external') {
            return (
              <a
                key={it.key}
                href={it.href}
                target="_blank"
                rel="noopener noreferrer"
                title={it.label}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 'var(--radius)',
                  background: 'transparent', color: 'var(--ink-2)',
                  textDecoration: 'none',
                  fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
                  marginBottom: 2, transition: 'all .15s ease',
                  whiteSpace: 'nowrap', boxSizing: 'border-box',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <it.icon size={18} strokeWidth={2.2} style={{ flexShrink: 0, color: 'var(--muted)' }} />
                <span className="sidebar-label" style={{ flex: 1, textAlign: 'left' }}>
                  {it.label}
                </span>
              </a>
            );
          }

          const active = current === it.key;
          return (
            <button
              key={it.key}
              onClick={() => setCurrent(it.key)}
              title={it.label}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 'var(--radius)',
                background: active ? 'var(--primary-soft)' : 'transparent',
                color: active ? 'var(--primary)' : 'var(--ink-2)',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13.5, fontWeight: 600, marginBottom: 2,
                position: 'relative', transition: 'all .15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={e => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
              onMouseOut={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
            >
              {active && (
                <span style={{
                  position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
                  width: 3, height: 18, background: 'var(--primary)', borderRadius: '0 3px 3px 0',
                }} />
              )}
              <it.icon size={18} strokeWidth={2.2} style={{
                flexShrink: 0,
                color: it.accent && !active ? 'var(--primary)' : undefined,
              }} />
              <span className="sidebar-label" style={{ flex: 1, textAlign: 'left' }}>
                {it.label}
              </span>
              {typeof it.badge === 'number' && it.badge > 0 && (
                <span className="sidebar-label" style={{
                  fontSize: 10.5, fontWeight: 700, color: active ? 'var(--primary)' : 'var(--muted)',
                  background: active ? 'var(--primary-soft-2)' : 'var(--surface-2)',
                  padding: '2px 7px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--primary-line)' : 'var(--border)'}`,
                }}>{it.badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div style={{ padding: 14, borderTop: '1px solid var(--border)' }}>
        <div className="sidebar-user-card" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 10, transition: 'all .2s ease',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 999, background: 'var(--primary-soft-2)',
            color: 'var(--primary)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontWeight: 700, fontSize: 12.5, flexShrink: 0,
            border: '1px solid var(--primary-line)',
          }}>{initials}</div>

          <div className="sidebar-user-extra" style={{
            flex: 1, alignItems: 'center', gap: 10, minWidth: 0,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 12.5, fontWeight: 600, lineHeight: 1.2,
                textTransform: 'capitalize',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{displayName || '—'}</div>
              <div style={{
                fontSize: 11, color: 'var(--muted)', lineHeight: 1.2, marginTop: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{userEmail || '—'}</div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <ThemeToggle size={14} />
              <button
                onClick={handleLogout}
                title="Sair"
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: 'var(--muted)', padding: 6, borderRadius: 6,
                  display: 'inline-flex', alignItems: 'center',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--danger)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--muted)'; }}
              >
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

export const SIDEBAR_COLLAPSED_WIDTH = COLLAPSED_W;
export const SIDEBAR_EXPANDED_WIDTH  = EXPANDED_W;
